/**
 * Support Request Types for DAFNE Log
 * Defines types for patient-clinician support communication
 */

// Priority levels for support requests
export const SupportRequestPriority = {
  Question: "question",
  HelpThisWeek: "help-this-week",
  Urgent: "urgent",
} as const;

export type SupportRequestPriorityType =
  (typeof SupportRequestPriority)[keyof typeof SupportRequestPriority];

// Status values for support requests
export const SupportRequestStatus = {
  Open: "open",
  InProgress: "in-progress",
  Resolved: "resolved",
} as const;

export type SupportRequestStatusType =
  (typeof SupportRequestStatus)[keyof typeof SupportRequestStatus];

// Priority display info
export const PRIORITY_INFO: Record<
  SupportRequestPriorityType,
  { label: string; description: string; color: string; icon: string }
> = {
  question: {
    label: "Question",
    description: "General question, no urgency",
    color: "#2196f3", // blue
    icon: "circle-question",
  },
  "help-this-week": {
    label: "Help This Week",
    description: "Would like help within a few days",
    color: "#ff9800", // orange
    icon: "circle-exclamation",
  },
  urgent: {
    label: "Urgent",
    description: "Need immediate attention",
    color: "#f44336", // red
    icon: "triangle-exclamation",
  },
};

// Status display info
export const STATUS_INFO: Record<
  SupportRequestStatusType,
  { label: string; color: string }
> = {
  open: { label: "Open", color: "#2196f3" },
  "in-progress": { label: "In Progress", color: "#ff9800" },
  resolved: { label: "Resolved", color: "#4caf50" },
};

/**
 * Support request as returned from the API
 */
export interface SupportRequest {
  requestId: string;
  clinicGroupId: string;
  patientId: string;
  patientName: string;
  patientEmail?: string;
  priority: SupportRequestPriorityType;
  subject: string;
  initialNote?: string;
  status: SupportRequestStatusType;
  createdAt: string;
  respondedAt?: string;
  resolvedAt?: string;
  assignedToId?: string;
  assignedToName?: string;
  resolvedById?: string;
  resolvedByName?: string;
  resolutionNote?: string;
  responseCount: number;
}

/**
 * Support response/message from a clinician
 */
export interface SupportResponse {
  responseId: string;
  clinicianId: string;
  clinicianName: string;
  message: string;
  createdAt: string;
}

/**
 * Detailed support request with conversation history
 */
export interface SupportRequestDetail {
  request: SupportRequest;
  responses: SupportResponse[];
}

/**
 * Request to create a new support request
 */
export interface CreateSupportRequestRequest {
  clinicGroupId: string;
  priority: SupportRequestPriorityType;
  subject: string;
  message: string;
}

/**
 * Request to add a response to a support request
 */
export interface AddSupportResponseRequest {
  message: string;
}

/**
 * Request to resolve a support request
 */
export interface ResolveSupportRequestRequest {
  resolutionNote?: string;
}
